import { ConcurrencyTarget } from "../../models/concurrencyTargetEnum";
import { DomainType } from "../../models/domain";
import { InterfaceModes } from "../../models/encounterEntity";
import { legacyPostMessage } from "../../utils/interface";
import { HttpMethod, deleteEndpoint, get, patch, post, withBodyMethod } from "../../utils/net";

import {
  createFilterString,
  getEncounterChanges,
  mapEncounterResultToEntity,
  mapSearchServiceEncounterResultToEntity,
} from "./encounterMapping";

import type { CreateEncounterState } from "../../models/createEncounter";
import type { DomainTypeEnum } from "../../models/domain";
import type {
  EncounterEntity,
  EncounterExportParams,
  EncounterExportStateResponse,
  EncounterImport,
  EncounterImportExportFormats,
  EncounterImportParams,
  EncounterSnapshotEntity,
  EncounterState,
  InterfacePushOptions,
  RecoveredEncountersResult,
} from "../../models/encounterEntity";
import type {
  EncounterSearchServiceResultEntity,
  JSONPATCH,
  ServiceEncounterEntity,
} from "../../models/encounterServiceEntity";
import type { DRGAnalyzeResultEntity } from "../../models/groupingEntity";
import type { PaginationList } from "../../models/pagination";
import type { ChoiceListsState, IdDescriptionBase } from "../../models/patientEncounter";
import type { SearchEncounterParams } from "../../models/searchEncounterFilter";
import type { EncounterChoiceListsUpdate } from "../../scenes/Encounter/actions/encounterFieldChange";
import type { ExtraOptions } from "../tee";

const baseURL = process.env.REACT_APP_API_PATH;

interface OpenEncounterByIdResponse {
  encounter: EncounterEntity;
  serviceEncounter: ServiceEncounterEntity;
}

const openEncounterById = (
  id: string,
  facilities: IdDescriptionBase[],
): Promise<OpenEncounterByIdResponse> => {
  const url = `${baseURL}/api/CurrentEncounter/Get/${id}`;

  const extraOptions = {
    concurrencyTarget: ConcurrencyTarget.Encounter,
    useExactPath: true,
  };

  return get(url, extraOptions).then((result) => ({
    encounter: mapEncounterResultToEntity(result.body, facilities),
    serviceEncounter: result.body,
  }));
};

// *** Search encounters section ***
const searchEncounters = async (
  params: SearchEncounterParams,
): Promise<{ encounters: EncounterEntity[]; totalCount: number }> => {
  const filterStr = createFilterString(params.filter);
  const urlParams = new URLSearchParams(filterStr);

  // Apply sort parameters if provided, otherwise use default sorting
  const sortEntries = Object.entries(params.sort || {});
  if (sortEntries.length > 0) {
    sortEntries.forEach(([key, value]) => {
      urlParams.append(key, value.toString());
    });
  } else {
    // Default sorting: MedicalRecordNumber ascending, AdmitDate descending
    urlParams.append("SortAsc.MedicalRecordNumber", "1");
    urlParams.append("SortDesc.AdmitDate", "2");
  }

  let url = `${baseURL}/api/encounters/search?${urlParams}`;

  if (params.domain) {
    url = `${baseURL}/api/encounters/search/${params.domain === DomainType.Practice ? "Practices" : "Facilities"}?${urlParams}`;
  }

  const { Items, TotalCount } = await getPaginationList<EncounterSearchServiceResultEntity>(
    url,
    params.top,
    params.skip,
  );
  return {
    encounters: mapSearchServiceEncounterResultsToEntity(Items),
    totalCount: TotalCount,
  };
};

async function getPaginationList<T>(
  url: string,
  top: number,
  skip: number,
): Promise<PaginationList<T>> {
  const params = new URLSearchParams();
  params.append("count", "true");
  params.append("top", top.toString());
  params.append("skip", skip.toString());

  const paginatedUrl = `${url}${url.includes("?") ? "&" : "?"}${params}`;

  const extraOptions = {
    headers: { Accept: "text/html" },
    useExactPath: true,
    // used to cancel promise to prevent overlapping search results
    singletonKey: "SEARCH_ENCOUNTERS",
  };

  const response = await get(paginatedUrl, extraOptions);
  return response.body as PaginationList<T>;
}

const mapSearchServiceEncounterResultsToEntity = (
  list: EncounterSearchServiceResultEntity[],
): EncounterEntity[] => {
  const encounters = list.map((item) => mapSearchServiceEncounterResultToEntity(item));

  return encounters;
};

// *** Save encounter section ***
const saveOrPullEncounter = (
  id: string,
  version?: string,
  isInterfaced?: boolean,
  InterfaceMode?: string,
): Promise<any> => {
  const isLegacyInterface = InterfaceMode === InterfaceModes.Legacy;
  const url =
    isInterfaced && isLegacyInterface ?
      `${baseURL}/api/Interface/Pull/${id}`
    : `${baseURL}/api/CurrentEncounter/Save/${id}`;

  const extraOptions = {
    concurrencyTarget: ConcurrencyTarget.Encounter,
    headers: {
      "If-Match": version,
    },
    useExactPath: true,
  };

  if (isInterfaced && isLegacyInterface) {
    return get(url, extraOptions).then((result) => result.text);
  }

  return post(url, {}, extraOptions).then((result) => result.body);
};

// *** Save encounter changes section ***
const saveEncounterChanges = (
  encounter: EncounterState,
  choiceLists: ChoiceListsState,
  changesToSave: JSONPATCH[] | null,
): Promise<any> => {
  const url = `${baseURL}/api/CurrentEncounter/Change/${encounter.inProgress.id}`;

  const extraOptions = {
    // FIXME: shouldn't this be Changeset? arent we throwing away the response?
    concurrencyTarget: ConcurrencyTarget.Encounter,
    headers: {
      "If-Match": encounter.inProgress.concurrencyVersion || "",
    },
    method: "PATCH",
    useExactPath: true,
  };

  // we can have already calculated changes: use them instead of the calculating
  const data = changesToSave || getEncounterChanges(encounter, choiceLists);

  return patch(url, data, extraOptions).then((result) => result.body);
};

// *** Create encounter section ***
const createEncounter = (newEncounterData: CreateEncounterState): Promise<EncounterEntity> => {
  const url = `${baseURL}/api/CurrentEncounter/Create`;
  const data = {
    EncounterTypeId: newEncounterData.encounterType,
    FacilityId: newEncounterData.facility,
    MedicalRecordNumber: newEncounterData.mrn,
    AccountNumber: newEncounterData.accountNumber,
  };
  const extraOptions = {
    concurrencyTarget: ConcurrencyTarget.Encounter,
    useExactPath: true,
  };

  return post(url, data, extraOptions).then((result) =>
    mapEncounterResultToEntity(result.body, newEncounterData.choiceLists.facilities),
  );
};

// *** Get encounter section ***
const getEncounter = (
  encounter: EncounterState,
  facilities: IdDescriptionBase[],
): Promise<EncounterEntity> => {
  const url = `${baseURL}/api/CurrentEncounter/Get/${encounter.inProgress.id}`;

  const extraOptions = {
    concurrencyTarget: ConcurrencyTarget.Encounter,
    useExactPath: true,
  };

  return get(url, extraOptions).then((result) =>
    mapEncounterResultToEntity(result.body, facilities),
  );
};

// *** Export encounter format types ***
const getEncounterImportExportFormats = (): Promise<EncounterImportExportFormats[]> => {
  const url = `${baseURL}/api/encounters/formats`;

  const extraOptions = {
    useExactPath: true,
    skipContentTypeHeader: true,
  };

  return get(url, extraOptions).then((result) => result.body);
};

// *** Start Encounter Export Job ***
const exportEncounterItems = (
  params: EncounterExportParams,
): Promise<EncounterExportStateResponse> => {
  const url = `${baseURL}/api/encounters/export`;

  const extraOptions = {
    useExactPath: true,
  };

  return post(url, params, extraOptions).then((result) => result.body);
};

// *** Check Encounter Export Job Status ***
const getExportEncounterStatus = (taskId: string): Promise<EncounterExportStateResponse> => {
  const url = `${baseURL}/api/encounters/export/${taskId}`;

  const extraOptions = {
    useExactPath: true,
  };

  return get(url, extraOptions).then((result) => result.body);
};

// *** Get Encounter Export File for Downloading ***
const getExportEncounterFile = async (taskId: string): Promise<Blob> => {
  const url = `${baseURL}/api/encounters/export/${taskId}/file`;

  const extraOptions = {
    useExactPath: true,
    responseType: "blob",
  };

  const response = await get(url, extraOptions);

  return response.body;
};

// *** Cancelling Encounter Export job and cleaning up taken resources ***
const deleteExportEncounterJob = (taskId: string): Promise<string> => {
  const url = `${baseURL}/api/encounters/export/${taskId}`;

  const extraOptions = {
    useExactPath: true,
  };

  return deleteEndpoint(url, extraOptions).then((result) => result.body);
};

// *** Importing encounters  ***
const importEncounters = (params: EncounterImportParams): Promise<EncounterImport> => {
  const queryParams = new URLSearchParams();
  queryParams.append("formatName", params.FormatName);

  const formData = new FormData();
  formData.append("file", params.File);
  formData.append("fileName", params.File.name);

  const url = `${baseURL}/api/encounters/import?${queryParams}`;

  const extraOptions = {
    useExactPath: true,
    skipContentTypeHeader: true,
  };

  return post(url, formData, extraOptions).then((result) => result.body);
};

// *** Get status of importing encounters  ***
const getStatusImportingEncounters = (taskId: string): Promise<EncounterImport> => {
  const url = `${baseURL}/api/encounters/import/${taskId}`;

  const extraOptions = {
    useExactPath: true,
  };

  return get(url, extraOptions).then((result) => result.body);
};

// *** Cancel importing encounters  ***
const cancelImportEncounters = (taskId: string): Promise<string> => {
  const url = `${baseURL}/api/encounters/import/${taskId}`;

  const extraOptions = {
    useExactPath: true,
  };

  return deleteEndpoint(url, extraOptions).then((result) => result.body);
};

// *** Snapshot encounter section ***
const snapshotEncounter = (id: string): Promise<EncounterSnapshotEntity> => {
  // TODO: remove workaround for russian "c"
  const url = `${baseURL}/api/CurrentEncounter/CaptureSnapshot/${id}`;

  const extraOptions = {
    concurrencyTarget: ConcurrencyTarget.Encounter,
    useExactPath: true,
  };

  return get(url, extraOptions).then((result) => result.body);
};

// *** Close encounter section ***
const closeEncounter = (id: string, concurrencyVersion?: string): Promise<string> => {
  const version = concurrencyVersion || "";
  const url = `${baseURL}/api/CurrentEncounter/Close/${id}`;

  const headers = {
    "If-Match": version,
  };
  const extraOptions = {
    headers,
    useExactPath: true,
  };

  const data = {};

  return (
    post(url, data, extraOptions)
      // TODO: analyze result and parse errors
      .then(() => mapEncounterCloseResultToEntity())
  );
};

const mapEncounterCloseResultToEntity = (): string =>
  // TODO: I have empty result here
  "Success";
// *** Process encounter section ***
const processEncounter = async (
  encounterEntity: EncounterEntity,
  groupingType?: string,
  previousKey?: string,
) => {
  const groupingResultPart = groupingType ? `groupingResultId=${groupingType}` : "";
  const previousKeyPart = previousKey ? `key=${previousKey}` : "";
  const groupingPart =
    groupingResultPart || previousKeyPart ?
      `?${groupingResultPart}${groupingResultPart && previousKeyPart ? "&" : ""}${previousKeyPart}`
    : "";
  const url = `${process.env.REACT_APP_API_PATH}/api/CurrentEncounter/Process/${encounterEntity.id}${groupingPart}`;

  const extraOptions = {
    concurrencyTarget: ConcurrencyTarget.Encounter,
    headers: {
      "If-Match": encounterEntity.concurrencyVersion || "",
    },
    useExactPath: true,
  };

  const response = await post(url, {}, extraOptions);

  return response.body;
};

const analyzeEncounter = (encounterEntity: EncounterEntity): Promise<DRGAnalyzeResultEntity[]> => {
  const version = encounterEntity.concurrencyVersion || "";
  const url = `${baseURL}/api/CurrentEncounter/DRGAnalyze/${encounterEntity.id}?groupingResultId=${encounterEntity.ipGroupingType}`;

  const headers = {
    "If-Match": version,
  };

  const extraOptions = {
    concurrencyTarget: ConcurrencyTarget.Encounter,
    headers,
    useExactPath: true,
  };

  return get(url, extraOptions).then((result) => result.body);
};

const processMNE = (encounterEntity: EncounterEntity, previousKey?: string): Promise<any> => {
  const { id } = encounterEntity;
  const version = encounterEntity.concurrencyVersion || "";
  const groupingPart = previousKey ? `?key=${previousKey}` : "";
  const url = `${baseURL}/api/CurrentEncounter/ProcessMNE/${id}${groupingPart}`;

  const headers = {
    "If-Match": version,
  };

  const extraOptions = {
    concurrencyTarget: ConcurrencyTarget.Encounter,
    headers,
    useExactPath: true,
  };

  const data = {};

  console.log("Process MNE");

  return post(url, data, extraOptions).then((result) => result.body);
};

const processPFS = async (encounterEntity: EncounterEntity, previousKey?: string): Promise<any> => {
  const { id } = encounterEntity;
  const version = encounterEntity.concurrencyVersion || "";
  const groupingPart = previousKey ? `?key=${previousKey}` : "";
  const url = `${baseURL}/api/CurrentEncounter/ProcessPFS/${id}${groupingPart}`;

  const headers = {
    "If-Match": version,
  };

  const extraOptions = {
    concurrencyTarget: ConcurrencyTarget.Encounter,
    headers,
    useExactPath: true,
  };

  const data = {};

  const result = await post(url, data, extraOptions);
  return result.body;
};

// *** Heartbeat section ***
const heartbeatEncounter = (encounter: EncounterState): Promise<string> => {
  const version = encounter.inProgress.concurrencyVersion || "";
  const url = `${baseURL}/api/CurrentEncounter/heartbeat/${encounter.inProgress.id}`;

  const headers = {
    "If-Match": version,
  };
  const extraOptions = {
    headers,
    useExactPath: true,
  };

  return (
    get(url, extraOptions)
      // FIXME: check result status 2xx?
      .then(() => "Success")
  );
};

// map choice lists values to new facility
const mapChoiceListsToNewFacility = (
  facility: string,
  inProgress: EncounterEntity,
): Promise<EncounterChoiceListsUpdate> => {
  const version = inProgress.concurrencyVersion || "";
  const url = `${baseURL}/api/CurrentEncounter/facilityMapInfo/${inProgress.id}?facilityid=${facility}`;

  const headers = {
    "If-Match": version,
  };
  const extraOptions = {
    headers,
    useExactPath: true,
  };

  return get(url, extraOptions).then((result) => result.body);
};

// redirect in interfaced mode
const redirectInInterfacedMode = (inProgress: EncounterEntity): Promise<boolean> => {
  const version = inProgress.concurrencyVersion || "";
  const url = `${baseURL}/api/interface/redirect/${inProgress.id}`;

  const headers = {
    "If-Match": version,
  };
  const extraOptions = {
    headers,
    useExactPath: true,
  };

  return get(url, extraOptions).then((result) => true);
};

const interfacePush = (payload: string, options?: InterfacePushOptions): Promise<any> => {
  const url = `${baseURL}/api/Interface/Push${options?.debug ? "?callback=http://fake.callback" : ""}`;
  const extraOptions: ExtraOptions = {
    concurrencyTarget: ConcurrencyTarget.Encounter,
    useExactPath: true,
    headers: {
      "Content-Type": "text/plain",
    },
  };

  return post(url, payload, extraOptions).then((result) => result.body);
};

// cancel in interfaced mode
const cancelInterfacedEncounter = (encounterId: string, version?: string): Promise<boolean> => {
  const url = `${baseURL}/api/interface/cancel/${encounterId}`;

  // const version = inProgress.concurrencyVersion || '';
  // 'If-Match': version,

  const headers = {};
  if (version) {
    headers["If-Match"] = version;
  }

  const extraOptions = {
    headers,
    useExactPath: true,
  };

  return post(url, {}, extraOptions).then((result) => {
    legacyPostMessage({ type: "canceled" });
    return result.body;
  });
};

// Get Recovered Encounters
interface GetRecoveredEncountersArgs {
  domain: DomainTypeEnum;
  top?: number;
  skip?: number;
  sort?: Record<string, number>;
}

const domainUrl = {
  [DomainType.Facility]: "Facilities",
  [DomainType.Practice]: "Practices",
};

export const getRecoveredEncounters = ({
  top,
  skip,
  sort,
  domain,
}: GetRecoveredEncountersArgs): Promise<RecoveredEncountersResult> => {
  const params = new URLSearchParams({
    Count: true,
    Top: top,
    Skip: skip,
    ...(sort ?? {}),
  });

  const url = `${baseURL}/api/${domainUrl[domain]}/recovery/getSessions?${params.toString()}`;

  const extraOptions = {
    useExactPath: true,
  };

  return get(url, extraOptions).then((result) => result.body);
};

// Remove Recovered Encounters
export const removeRecoveredEncounter = (
  domain: DomainTypeEnum,
  encounterIds: string[],
): Promise<{ Deleted: number; UniqueCount: number }> => {
  const url = `${baseURL}/api/${domainUrl[domain]}/recovery/removeSession`;

  const extraOptions = {
    useExactPath: true,
  };

  return withBodyMethod(url, encounterIds, extraOptions, HttpMethod.Delete).then(
    (result) => result.body,
  );
};

// Get Recovered Encounters Total
export const getRecoveredEncountersCount = (
  domain: DomainTypeEnum,
): Promise<RecoveredEncountersResult> => {
  const url = `${baseURL}/api/${domainUrl[domain]}/recovery/getSessions?Top=0&Count=true`;

  const extraOptions = {
    useExactPath: true,
  };

  return get(url, extraOptions).then((result) => result.body);
};

// Check if encounter is recovered
const checkEncounterIsRecovered = (encounterId: string): Promise<boolean> => {
  const url = `${baseURL}/api/recovery/getSessions?Match.EncounterId=${encounterId}`;

  const extraOptions = {
    useExactPath: true,
  };

  return get(url, extraOptions).then((result) => result?.body?.Items?.length > 0);
};

const deleteEncounter = (encounterIds: string[]): Promise<{ Deleted: number }> => {
  const url = `${baseURL}/api/encounters/delete`;

  const extraOptions = {
    useExactPath: true,
  };

  return post(url, encounterIds, extraOptions).then((result) => result.body);
};

export const encounterAPI = {
  analyzeEncounter,
  closeEncounter,
  createEncounter,
  getEncounter,
  heartbeatEncounter,
  openEncounterById,
  processEncounter,
  processMNE,
  processPFS,
  saveOrPullEncounter,
  saveEncounterChanges,
  searchEncounters,
  snapshotEncounter,
  mapChoiceListsToNewFacility,
  redirectInInterfacedMode,
  cancelInterfacedEncounter,
  interfacePush,
  getRecoveredEncounters,
  removeRecoveredEncounter,
  getRecoveredEncountersCount,
  checkEncounterIsRecovered,
  getEncounterImportExportFormats,
  exportEncounterItems,
  getExportEncounterStatus,
  importEncounters,
  getStatusImportingEncounters,
  cancelImportEncounters,
  getExportEncounterFile,
  deleteExportEncounterJob,
  deleteEncounter,
};
